import React, {useEffect, useState} from 'react';

import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';

import { useRouter } from 'next/router';

import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Hidden
} from '@mui/material';

import Skeleton from '@mui/material/Skeleton';

import axios from 'axios';
import useSWR from 'swr';

import Error from '../../../_error';

import BottomNav from '../../../../components/BottomNav';
import HeroUnder from '../../../../components/HeroUnder';
import InfoCircle from '../../../../components/InfoCircle';
import TopNav from '../../../../components/TopNav';
import WaivingBuddy from '../../../../components/WaivingBuddy';

import { NextLinkComposed } from '../../../../components/Link';

import bodyCG from '../../../../public/partners/v2/500x200-compare-guru-scratch-dent-cover.png';
import mobileCG from '../../../../public/partners/v2/325x80-compare-guru-insurance.png';
import tyresCG from '../../../../public/partners/v2/500x200-compare-guru-tyre-rim-cover.png';
import warrantyCG from '../../../../public/partners/v2/500x200-compare-guru-pre-owned-warranty.png';

const DynamicCostEstimateSummary = dynamic(() => import ('../../../../components/CostEstimateSummary'));
const DynamicPersonalisedEstimate = dynamic(() => import ('../../../../components/PersonalisedEstimate'));


const ads = {
  warranty: {
    img: warrantyCG,
    href: 'https://compareguru.co.za/insurance-quotes/motorvaps/pre-owned-motor-warranty/personal?channel=mechanic-buddy',
  },
  tyres: {
    img: tyresCG,
    href: 'https://compareguru.co.za/insurance-quotes/motorvaps/tyre-and-rim-guard/personal?channel=mechanic-buddy',
  },
  body: {
    img: bodyCG,
    href: 'https://compareguru.co.za/insurance-quotes/motorvaps/scratch-and-dent/personal?channel=mechanic-buddy',
  },
}


function QuoteRequest({ data, statusCode }) {
  
  const [adType, setAdType] = useState(ads['warranty']);
  const router = useRouter();

  const { data: estimate } = useSWR(
    data && `/content/cost-estimate/?car=${data.car.make}&model=${data.car.model}&service=${data.name}`
  );

  useEffect(() => {
    if (data?.category == "Tyres, Wheels and Alignment") {
      setAdType(ads['tyres']);
    }

    if (data?.category == "Bodywork and Dent Repairs" || data?.category == "Windows, Windscreens and Mirrors") {
      setAdType(ads['body']);
    }
  }, [data])

  if (statusCode) {
    return <Error statusCode={statusCode} />
  }

  const estimateURL = {
    pathname: '/cost-estimates/[car]/[model]/[service]/',
    query: {
      car: data.car.make,
      model: data.car.model,
      service: data.name
    }
  }

  const pageDescription = `The quote request was posted on ${data.created} and received ${data.estimate} cost estimates from local mechanics and workshops with an average of R${data.avg_estimate}.`

  return <>
    <Head>
      <title>{data.name} Cost Estimates for a {data.car_string} | MechanicBuddy</title>
      <meta
        name="description"
        content={pageDescription}
      />
    </Head>

    <TopNav />

    <HeroUnder
      title={data.name}
      description={data.car_string}
    />
    
    <Container
      maxWidth="lg"
      sx={{
        mt: '-120px',
        mb: 8
      }}
    >
      <Grid
        container
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={5}
      >
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ fontSize: 'body2.fontSize' }}>
              <Box my={1} fontSize="h6.fontSize" color="primary.light">Quote Request</Box>

              <Box my={2}>
                You are viewing the details of a request to receive quotes, posted by another user. 
                We have made a summary of the quotes received from mechanics and workshops so that you can get an estimated cost.
              </Box>

              <Box display={{ xs: 'block', md: 'none'}} my={2}>
                <Box display="flex" flexDirection="row">
                  <Box
                    bgcolor="primary.light"
                    display="flex"
                    flexDirection="column"
                    color="white"
                    p={4}
                    flexGrow={1}
                    alignItems="center"
                    mr={1}
                  >
                    <Box fontSize="h6.fontSize">{data.estimate_count}</Box>
                    <Box textAlign="center">Estimates<br />Received</Box>
                  </Box>
                  <Box
                    bgcolor="primary.light"
                    display="flex"
                    flexDirection="column"
                    color="white"
                    p={4}
                    flexGrow={1}
                    alignItems="center"
                    ml={1}
                  >
                    <Box fontSize="h6.fontSize">R{data.avg_estimate}</Box>
                    <Box textAlign="center">Average<br />Estimate</Box>
                  </Box>
                </Box>
                <Box
                    bgcolor="primary.light"
                    display="flex"
                    flexDirection="column"
                    color="white"
                    p={4}
                    alignItems="center"
                    width="100%"
                    mt={2}
                  >
                    <Box fontSize="h6.fontSize">R{data.lowest_estimate} - R{data.highest_estimate}</Box>
                    <Box textAlign="center">Quote Range</Box>
                  </Box>
              </Box>

              <Box display={{ xs: 'none', md: 'flex' }} flexDirection="row" justifyContent="center" mb={5}>
                <InfoCircle value={data.estimate_count} title={<>Quotes<br />Received</>} />
                <InfoCircle value={'R' + data.avg_estimate} title={<>Average<br />Estimate</>} />
                <InfoCircle value={'R' + data.highest_estimate} title={<>Highest<br />Estimate</>} />
                <InfoCircle value={'R' + data.lowest_estimate} title={<>Lowest<br />Estimate</>} />
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box mb={1} fontWeight="fontWeightMedium">Vehicle</Box>
                  <Box>{data.car_string}</Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box mb={1} fontWeight="fontWeightMedium">Location</Box>
                  <Box>{data.where}</Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box mb={1} fontWeight="fontWeightMedium">Date Requested</Box>
                  <Box>{data.created}</Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box mb={1} fontWeight="fontWeightMedium">Service</Box>
                  <Box>{data.name}</Box>
                </Grid>

                <Grid item xs={12}>
                  <Box mb={1} fontWeight="fontWeightMedium">
                    {data.helper ? data.helper : 'Service'}
                  </Box>
                  <Box>{data.description}</Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Box
            width="100%"
            display={{xs: "flex", md: "none"}}
            flexDirection="row"
            justifyContent="center"
            my={2}
          >
            <a
              href="https://compareguru.co.za/insurance-quotes/car-insurance/personal?channel=mechanic-buddy"
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              <Image
                src={mobileCG}
                alt="CompareGuru insurance"
                quality={100}
              />
            </a>
          </Box>

          <Box
            sx={{
              fontSize: 'body2.fontSize',
              width: "100%",
              bgcolor: "primary.main",
              p: 3,
              display: {
                xs: 'none',
                md: 'flex'
              },
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              my: 2,
            }}
          >
            <Box color="white">Get a quick estimate for a {data.name} on a {data.car.make} {data.car.model}</Box>
            <Button
              variant="contained"
              color="secondary"
              component={NextLinkComposed}
              to={estimateURL}
            >Show Estimate</Button>
          </Box>

          <Card>
            <CardContent sx={{ cursor: 'pointer' }} onClick={() => router.push(estimateURL)}>
              {estimate ?
                <DynamicCostEstimateSummary
                  make={data.car.make}
                  model={data.car.model}
                  service={data.name}
                  estimate={estimate}
                /> :
                <Skeleton variant="rectangular" width="100%" height={250} />
              }

            </CardContent>

            <Box
              width="100%"
              bgcolor="primary.main"
              p={3}
              display={{ xs: 'flex', md: 'none' }}
              flexDirection="column"
              alignItems="center"
            >
              <Box
                textAlign="center"
                fontSize="subtitle1.fontSize"
                color="white"
                mb={2}>Get quotes for a {data.name} on a {data.car.make} {data.car.model}
              </Box>
              <Box display="flex" flexDirection="row" justifyContent="space-between">
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{
                    border: "1px solid white",
                    marginRight: "8px",
                    color: "white"
                  }}
                  component={NextLinkComposed}
                  to={estimateURL}
                >Show Estimate</Button>

                <Button
                  variant="contained"
                  color="secondary"
                  component={NextLinkComposed}
                  to={{ pathname: "/request-quotes/" }}
                >Get Quotes</Button>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Hidden mdDown implementation="css">
            <a
              href={adType.href}
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              <Image
                src={adType.img}
                alt="CompareGuru"
                quality={100}
              />
            </a>

            <Box p={2} />

            <DynamicPersonalisedEstimate
              initialMake={data.car.make}
              initialModel={data.car.model}
              initialService={data.name}
            />

          </Hidden>
        </Grid>
      </Grid>
    </Container>

    <WaivingBuddy />

    <BottomNav />
  </>;
}


export async function getStaticProps({ params }) {
  const props = await axios(
    `${process.env.DOMAIN}/content/jobs/${params.id}/`
  )
  .then(result => {
    return {
      data: result.data,
      statusCode: null
    }
  })
  .catch(error => {
    return {
      data: null,
      statusCode: error.response?.status
    }
  });

  if (props.statusCode === 404) {
    return {
      notFound: true
    }
  }

  return {
    props,
    revalidate: 86400, // In seconds
  }
}


export async function getStaticPaths() {
  const { data } = await axios(`${process.env.DOMAIN}/content/jobs/?page_size=100`);

  const paths = data.results.map(job => ({
    params: {
      category: job.category_slug,
      job: job.name_slug,
      id: `${job.id}`,
    }
  }))

  return {
    paths,
    fallback: 'blocking'
  };
}


export default QuoteRequest;
